import { DefaultRootState } from 'react-redux';
import { applyMiddleware, combineReducers, compose, createStore, DeepPartial } from 'redux';
import createSagaMiddleware, { Saga } from 'redux-saga';
import { all } from 'redux-saga/effects';
import { objectMerge } from 'utils/objects';
import { widgetStore } from 'utils/widgetStore';

import { agentsReducer } from './agents/agents.reducer';
import { backgroundOperationReducer } from './backgroundOperations/backgroundOperations.reducer';
import { channelsReducer } from './channels/channels.reducer';
import { channelsSaga } from './channels/channels.saga';
import { chatReducer } from './chat/chat.reducer';
import { chatSaga } from './chat/chat.saga';
import { clientVoiceReducer } from './clientVoice/clientVoice.reducer';
import { clientVoiceSaga } from './clientVoice/clientVoice.saga';
import { commandsSaga } from './commands/commands.saga';
import { connectionReducer } from './connection/connection.reducer';
import { connectionSaga } from './connection/connection.saga';
import { csiReducer } from './csi/csi.reducer';
import { csiSaga } from './csi/csi.saga';
import { featuresReducer } from './features/features.reducer';
import { filesReducer } from './files/files.reducer';
import { filesSaga } from './files/files.saga';
import { gameReducer } from './game/game.reducer';
import { gameSaga } from './game/game.saga';
import { historyReducer } from './history/history.reducer';
import { historySaga } from './history/history.saga';
import { inputWidgetReducer } from './inputWidget/inputWidget.reducer';
import { inputWidgetSaga } from './inputWidget/inputWidget.saga';
import { parametersReducer } from './parameters/parameters.reducer';
import { sharingReducer } from './sharing/sharing.reducer';
import { sharingSaga } from './sharing/sharing.saga';
import { slumberReducer } from './slumber/slumber.reducer';
import { slumberSaga } from './slumber/slumber.saga';

export type WidgetState = ReturnType<typeof rootReducer>;
export type WidgetStore = ReturnType<typeof initStore>;

declare module 'react-redux' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultRootState extends WidgetState {}
}

let rootReducer = combineReducers({
    features: featuresReducer,
    connection: connectionReducer,
    chat: chatReducer,
    clientVoice: clientVoiceReducer,
    inputWidget: inputWidgetReducer,
    history: historyReducer,
    agents: agentsReducer,
    files: filesReducer,
    csi: csiReducer,
    parameters: parametersReducer,
    game: gameReducer,
    asleep: slumberReducer,
    sharing: sharingReducer,
    backgroundOperations: backgroundOperationReducer,
    channels: channelsReducer,
});

export const enum WidgetStoreActionType {
    ForceUpdate = 'STATE/FORCE_UPDATE',
}

export type ForceUpdateStoreAction = ReturnType<typeof forceUpdateStore>;
export const forceUpdateStore = (diff: DeepPartial<DefaultRootState>) => ({
    type: WidgetStoreActionType.ForceUpdate,
    payload: diff,
});

if (DEVELOPMENT) {
    const rootReducerImpl = rootReducer;

    rootReducer = (state, action) => {
        if (action.type === WidgetStoreActionType.ForceUpdate)
            return objectMerge(state, action.payload);

        return rootReducerImpl(state, action);
    };
}

function* rootSaga() {
    yield all([
        connectionSaga(),
        chatSaga(),
        clientVoiceSaga(),
        inputWidgetSaga(),
        historySaga(),
        filesSaga(),
        csiSaga(),
        gameSaga(),
        slumberSaga(),
        commandsSaga(),
        sharingSaga(),
        channelsSaga(),
    ]);
}

export const initStore = (name: string, isTest = false, testSaga?: Saga) => {
    const sagaMiddleware = createSagaMiddleware();

    const enchancer = window.__REDUX_DEVTOOLS_EXTENSION__
        ? compose(
              applyMiddleware(sagaMiddleware),
              window.__REDUX_DEVTOOLS_EXTENSION__({ name, trace: true }),
          )
        : applyMiddleware(sagaMiddleware);

    const store = createStore(rootReducer, enchancer);

    if (isTest) {
        if (testSaga) {
            sagaMiddleware.run(testSaga);
        }
    } else {
        sagaMiddleware.run(rootSaga);
    }

    widgetStore.store = store;

    return store;
};
